import React, { useState, useEffect, useRef } from 'react';
import { getSocket } from '../services/socket';

// Appels internes (Karim 2026-10-20) — réception UNIQUEMENT : l'admin/le
// superviseur appelle, l'agent répond. WebRTC navigateur-à-navigateur relayé
// par le socket existant — AUCUN SIP, aucun impact sur les appels clients.
// Même config ICE que sipClient.js (coturn maison + google fallback,
// prouvée 2026-10-15) : les deux navigateurs peuvent être derrière du NAT.
const PC_CONFIG = {
  iceServers: [
    {
      urls: [
        'stun:100.50.242.50:3478',
        'turn:100.50.242.50:3478?transport=udp',
        'turn:100.50.242.50:3478?transport=tcp',
      ],
      username: 'dialer',
      credential: 'UCEiQmvGAaECTwLciYhk'
    },
    { urls: 'stun:stun.l.google.com:19302' },
  ],
};

export default function InterCallWidget() {
  const [incoming, setIncoming] = useState(null);   // { call_id, from }
  const [active, setActive] = useState(null);        // { call_id, from }
  const [toast, setToast] = useState(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  const cleanup = () => {
    try { pcRef.current && pcRef.current.close(); } catch (_) {}
    pcRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setActive(null);
    setIncoming(null);
  };

  useEffect(() => {
    const s = getSocket();

    const onIncoming = (d) => setIncoming(d);
    const onCancelled = () => { setIncoming(null); setToast('Appel annulé par l\'admin'); setTimeout(() => setToast(null), 3000); };
    const onAccepted = (d) => setActive(d);
    const onOffer = async (d) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const pc = new RTCPeerConnection(PC_CONFIG);
        pcRef.current = pc;
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
        pc.onicecandidate = (e) => { if (e.candidate) s.emit('intercall:ice', { call_id: d.call_id, candidate: e.candidate }); };
        pc.ontrack = (e) => { if (audioRef.current) { audioRef.current.srcObject = e.streams[0]; audioRef.current.play().catch(() => {}); } };
        await pc.setRemoteDescription(d.sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        s.emit('intercall:answer', { call_id: d.call_id, sdp: answer });
      } catch (e) {
        console.error('[InterCall] answer failed:', e.message);
        s.emit('intercall:end', { call_id: d.call_id });
        cleanup();
      }
    };
    const onIce = (d) => { if (pcRef.current && d.candidate) pcRef.current.addIceCandidate(d.candidate).catch(() => {}); };
    const onEnded = () => { cleanup(); setToast('Appel interne terminé'); setTimeout(() => setToast(null), 4000); };

    s.on('intercall:incoming', onIncoming);
    s.on('intercall:cancelled', onCancelled);
    s.on('intercall:accepted', onAccepted);
    s.on('intercall:offer', onOffer);
    s.on('intercall:ice', onIce);
    s.on('intercall:ended', onEnded);
    return () => {
      ['intercall:incoming', 'intercall:cancelled', 'intercall:accepted', 'intercall:offer', 'intercall:ice', 'intercall:ended']
        .forEach(ev => s.off(ev));
    };
  }, []);

  const accept = () => {
    const s = getSocket();
    s.emit('intercall:accept', { call_id: incoming.call_id });
    // active s'affiche au retour 'intercall:accepted' ; on garde `from` :
    setActive({ call_id: incoming.call_id, from: incoming.from });
  };
  const reject = () => {
    const s = getSocket();
    s.emit('intercall:reject', { call_id: incoming.call_id });
    setIncoming(null);
  };
  const hangup = () => {
    const s = getSocket();
    s.emit('intercall:end', { call_id: active?.call_id || incoming?.call_id });
    cleanup();
  };

  return (
    <>
      {/* Hidden sink for the remote audio — must exist before ontrack fires */}
      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />

      {incoming && !active && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(3,7,18,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0f172a', border: '2px solid #22d3ee', borderRadius: 16, padding: '28px 34px', textAlign: 'center', boxShadow: '0 0 60px rgba(34,211,238,0.25)' }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>📞</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#e2e8f0' }}>Appel interne</div>
            <div style={{ fontSize: 13.5, color: '#22d3ee', fontWeight: 700, margin: '6px 0 18px' }}>{incoming.from?.name || 'Admin'}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={accept} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: '#10b981', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>✓ Accepter</button>
              <button onClick={reject} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>✕ Refuser</button>
            </div>
          </div>
        </div>
      )}

      {active && (
        <div style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9998, background: '#0f172a', border: '1px solid #22d3ee', borderRadius: 12, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.45)' }}>
          <span style={{ fontSize: 18 }}>🎧</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Appel interne — {active.from?.name || 'Admin'}</span>
          <button onClick={hangup} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>Raccrocher</button>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#1e293b', border: '1px solid #475569', borderRadius: 10, padding: '8px 16px', fontSize: 12.5, color: '#e2e8f0', fontWeight: 600 }}>
          {toast}
        </div>
      )}
    </>
  );
}
